// User-defined symbols (modules, functions, top-level variables) from a
// parse of the editor text — feeds completion, hover, signature help and
// go-to-definition. Offsets are editor-document offsets.
import type { Program, Statement } from '../scad/ast';
import { stringifyExpression } from '../app/outline';

export interface SymbolParam {
  name: string;
  defaultText?: string;
}

export interface SymbolDef {
  name: string;
  kind: 'module' | 'function' | 'variable';
  params: SymbolParam[];
  /** Range of the whole definition. */
  from: number;
  to: number;
  /** Range of the defined name (jump target). */
  nameFrom: number;
  nameTo: number;
  /** Leading `//` or block comment right above the definition. */
  doc?: string;
  /** e.g. `module tube(id, wall = 2)` or `len = 15` */
  signature: string;
}

export interface SymbolTable {
  modules: Map<string, SymbolDef>;
  functions: Map<string, SymbolDef>;
  variables: Map<string, SymbolDef>;
}

export const EMPTY_SYMBOLS: SymbolTable = { modules: new Map(), functions: new Map(), variables: new Map() };

/** Comment block ending on the line right above `offset` (blank lines break it). */
function leadingDoc(source: string, offset: number): string | undefined {
  const lineStart = source.lastIndexOf('\n', offset - 1) + 1;
  const lines = source.slice(0, lineStart).split('\n');
  lines.pop(); // empty tail after the final '\n'
  const out: string[] = [];
  for (let i = lines.length - 1; i >= 0; i--) {
    const t = lines[i].trim();
    if (t.startsWith('//')) { out.unshift(t.replace(/^\/\/+\s?/, '')); continue; }
    if (t.endsWith('*/')) {
      // Walk back to the start of the block comment
      const block: string[] = [];
      for (let j = i; j >= 0; j--) {
        block.unshift(lines[j].trim());
        if (lines[j].includes('/*')) { i = j; break; }
      }
      out.unshift(...block.map(l => l.replace(/^\/\*+\s?|\s?\*+\/$|^\*\s?/g, '')).filter(Boolean));
      continue;
    }
    break;
  }
  const text = out.join('\n').trim();
  return text || undefined;
}

/** Locate the defined name after the `module` / `function` keyword. */
function nameRange(source: string, from: number, name: string): [number, number] {
  const idx = source.indexOf(name, from);
  return idx >= 0 && idx - from < 80 ? [idx, idx + name.length] : [from, from];
}

export function buildSymbolTable(program: Program, source: string): SymbolTable {
  const table: SymbolTable = { modules: new Map(), functions: new Map(), variables: new Map() };
  const paramsOf = (ps: { name: string; defaultValue?: Parameters<typeof stringifyExpression>[0] }[]): SymbolParam[] =>
    ps.map(p => ({ name: p.name, defaultText: p.defaultValue ? stringifyExpression(p.defaultValue) : undefined }));
  const sig = (ps: SymbolParam[]) => ps.map(p => (p.defaultText !== undefined ? `${p.name} = ${p.defaultText}` : p.name)).join(', ');

  const visit = (stmts: Statement[], topLevel: boolean): void => {
    for (const s of stmts) {
      if (s.type === 'ModuleDefinitionStatement') {
        const params = paramsOf(s.params);
        const [nameFrom, nameTo] = nameRange(source, s.from.offset, s.name);
        if (!table.modules.has(s.name)) {
          table.modules.set(s.name, {
            name: s.name, kind: 'module', params, from: s.from.offset, to: s.to.offset, nameFrom, nameTo,
            doc: leadingDoc(source, s.from.offset), signature: `module ${s.name}(${sig(params)})`,
          });
        }
        visit(s.body, false);
      } else if (s.type === 'FunctionDefinitionStatement') {
        const params = paramsOf(s.params);
        const [nameFrom, nameTo] = nameRange(source, s.from.offset, s.name);
        if (!table.functions.has(s.name)) {
          table.functions.set(s.name, {
            name: s.name, kind: 'function', params, from: s.from.offset, to: s.to.offset, nameFrom, nameTo,
            doc: leadingDoc(source, s.from.offset), signature: `function ${s.name}(${sig(params)})`,
          });
        }
      } else if (s.type === 'AssignmentStatement' && topLevel) {
        // OpenSCAD: the last top-level assignment wins
        table.variables.set(s.name, {
          name: s.name, kind: 'variable', params: [], from: s.from.offset, to: s.to.offset,
          nameFrom: s.from.offset, nameTo: s.from.offset + s.name.length,
          doc: leadingDoc(source, s.from.offset), signature: `${s.name} = ${stringifyExpression(s.value)}`,
        });
      } else if (s.type === 'ModuleCallStatement') {
        visit(s.children, false);
      } else if (s.type === 'IfStatement') {
        visit(s.thenBody, topLevel);
        visit(s.elseBody, topLevel);
      } else if (s.type === 'ForStatement') {
        visit(s.body, false);
      }
    }
  };
  visit(program.statements, true);
  return table;
}

/**
 * Call context at `pos`: the callee name of the innermost unclosed `name(`
 * and the 0-based index of the argument the cursor is in (top-level commas
 * only; strings and nested brackets are skipped). Null outside a call.
 */
export function findCallContext(doc: string, pos: number): { name: string; argIndex: number; openParen: number } | null {
  let depth = 0;
  let commas = 0;
  for (let i = pos - 1; i >= 0; i--) {
    const ch = doc[i];
    if (ch === '"') {
      // skip back over a string literal
      i--;
      while (i >= 0 && !(doc[i] === '"' && doc[i - 1] !== '\\')) i--;
      continue;
    }
    if (ch === ')' || ch === ']') depth++;
    else if (ch === '[') {
      // Cursor is inside a vector literal that is (part of) an argument:
      // its commas don't separate call arguments.
      if (depth === 0) commas = 0;
      else depth--;
    } else if (ch === '(') {
      if (depth === 0) {
        let j = i - 1;
        while (j >= 0 && (doc[j] === ' ' || doc[j] === '\t')) j--;
        const end = j + 1;
        while (j >= 0 && /[a-zA-Z0-9_$]/.test(doc[j])) j--;
        const name = doc.slice(j + 1, end);
        if (name && /^[a-zA-Z_$]/.test(name)) return { name, argIndex: commas, openParen: i };
        commas = 0; // grouping parenthesis — keep looking outward
        continue;
      }
      depth--;
    } else if (ch === ',' && depth === 0) commas++;
    else if ((ch === ';' || ch === '{' || ch === '}') && depth === 0) return null;
  }
  return null;
}
